// Scenarios (SM-753): the documented catalogue with each scenario's users,
// seed ("SEED <name>") and reset ("RESET <name>", with invalidate_codes /
// clear_nonces), and the run history.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Check, Field, Select, TextInput } from "@/components/ui/Field";
import { Empty, JsonBlock, Mono, PageHeader, ReadOnlyHint, TableWrap } from "@/components/ui/Misc";
import { Skeleton } from "@/components/ui/Skeleton";
import { api } from "@/lib/api";
import { resetPhrase, seedPhrase } from "@/lib/confirm";
import { fmtTime } from "@/lib/format";
import { fetchers, keys } from "@/lib/queries";
import { useCanMutate } from "@/lib/session";
import type { Scenario, ScenarioRun } from "@/lib/types";

export const Route = createFileRoute("/_app/scenarios")({
  component: ScenariosPage,
});

type Pending = { action: "SEED" | "RESET"; scenario: Scenario } | null;

function ScenariosPage() {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const cat = useQuery({ queryKey: keys.scenarios(), queryFn: fetchers.scenarios });
  const clients = useQuery({ queryKey: keys.clients(), queryFn: fetchers.clients });
  const env = useQuery({ queryKey: keys.environment(), queryFn: fetchers.environment });
  const [pending, setPending] = useState<Pending>(null);
  const [clientId, setClientId] = useState("");
  const [invalidateCodes, setInvalidateCodes] = useState(true);
  const [clearNonces, setClearNonces] = useState(false);
  const [reason, setReason] = useState("");
  const [lastRun, setLastRun] = useState<ScenarioRun | null>(null);

  const clientIds = (clients.data ?? []).map((c) => c.client_id);
  const client = clientId || clientIds[0] || "";

  const open = (action: "SEED" | "RESET", scenario: Scenario) => {
    setInvalidateCodes(true);
    setClearNonces(false);
    setReason("");
    setPending({ action, scenario });
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Scenarios"
        subtitle={
          <>
            Seed restores a scenario's users (status and balance) without a reset. Reset opens a new scenario epoch
            {env.data ? ` (now ${env.data.epoch})` : ""}: the client's users are replaced, earlier codes stop working, the old
            epoch's fault rules stop applying. Transactions, request log, observations and audit survive.
          </>
        }
      />
      {!canMutate && <ReadOnlyHint />}
      <ErrorBanner error={cat.error} />
      {lastRun && (
        <div className="banner" role="status" style={{ borderColor: "var(--yes)" }}>
          <span aria-hidden>✓</span>
          <span>
            {lastRun.action} <b>{lastRun.scenario}</b> done — run #{lastRun.run_id}, epoch {lastRun.epoch}.{" "}
            <span className="mono text-xs">{JSON.stringify(lastRun.summary)}</span>
          </span>
        </div>
      )}
      {cat.isLoading ? (
        <Skeleton lines={8} />
      ) : (
        (cat.data?.scenarios ?? []).map((s) => (
          <SectionCard
            key={s.name}
            title={
              <span className="flex flex-wrap items-center gap-2">
                <Mono>{s.name}</Mono>
                <span className="text-xs font-normal t-mute">
                  {(s.users ?? []).length} users{s.load_users ? ` + ${s.load_users} load users at ${s.load_balance}` : ""}
                </span>
              </span>
            }
            actions={
              <>
                <Button size="sm" variant="ghost" disabled={!canMutate} onClick={() => open("SEED", s)}>
                  Seed
                </Button>
                <Button size="sm" variant="danger" disabled={!canMutate} onClick={() => open("RESET", s)}>
                  Reset
                </Button>
              </>
            }
          >
            <p className="mb-3 text-sm text-t-2">{s.description}</p>
            {(s.users ?? []).length === 0 ? (
              <Empty>Only generated load users.</Empty>
            ) : (
              <TableWrap>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>User id</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th className="text-right">Balance</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.users ?? []).map((u) => (
                      <tr key={u.user_id}>
                        <td>
                          <Mono>{u.user_id}</Mono>
                        </td>
                        <td>{u.display_name}</td>
                        <td>
                          <StatusBadge status={u.account_status} />
                        </td>
                        <td className="amt text-right">{u.balance}</td>
                        <td className="wrap">{u.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </SectionCard>
        ))
      )}

      <SectionCard title="Run history">
        <RunsTable rows={cat.data?.runs ?? []} />
      </SectionCard>

      <ConfirmDialog
        open={!!pending}
        title={pending ? `${pending.action === "SEED" ? "Seed" : "Reset"} scenario “${pending.scenario.name}”?` : ""}
        consequence={
          pending?.action === "RESET" ? (
            <>
              Opens scenario epoch <b>{(env.data?.epoch ?? 0) + 1}</b> for client <b className="mono">{client}</b>: every user of
              that client is <b>deleted</b> and replaced by the {(pending.scenario.users ?? []).length + (pending.scenario.load_users ?? 0)} documented
              users; fault rules of epoch {env.data?.epoch ?? "?"} stop applying. Transactions, the request log and audit are kept.
            </>
          ) : (
            <>
              Creates or restores the {(pending?.scenario.users ?? []).length + (pending?.scenario.load_users ?? 0)} users of this
              scenario for client <b className="mono">{client}</b> — their status and balance are <b>overwritten</b> with the
              documented values. No new epoch; other users are untouched.
            </>
          )
        }
        phrase={pending ? (pending.action === "SEED" ? seedPhrase(pending.scenario.name) : resetPhrase(pending.scenario.name)) : null}
        confirmLabel={pending?.action === "RESET" ? "Reset scenario" : "Seed scenario"}
        onConfirm={async (confirm) => {
          if (!pending) return;
          const name = encodeURIComponent(pending.scenario.name);
          const run =
            pending.action === "SEED"
              ? await api<ScenarioRun>(`/scenarios/${name}/seed`, { method: "POST", body: { client_id: client, confirm } })
              : await api<ScenarioRun>(`/scenarios/${name}/reset`, {
                  method: "POST",
                  body: { client_id: client, confirm, invalidate_codes: invalidateCodes, clear_nonces: clearNonces, reason: reason.trim() },
                });
          setLastRun(run);
          await qc.invalidateQueries({ queryKey: ["mock"] });
        }}
        onClose={() => setPending(null)}
      >
        <Field label="Client">
          <Select value={client} onChange={(e) => setClientId(e.target.value)}>
            {clientIds.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </Field>
        {pending?.action === "RESET" && (
          <>
            <Check
              label="Invalidate launch codes"
              hint="Every earlier code of this client becomes unusable — consumed ones too (no exact retry across a reset)."
              checked={invalidateCodes}
              onChange={(e) => setInvalidateCodes(e.target.checked)}
            />
            <Check
              label="Clear nonces"
              hint="Forget the 10-minute replay window, so a replayed nonce is accepted once again."
              checked={clearNonces}
              onChange={(e) => setClearNonces(e.target.checked)}
            />
            <Field label="Reason (optional, audited)">
              <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}

function RunsTable({ rows }: { rows: ScenarioRun[] }) {
  const [open, setOpen] = useState<number | null>(null);
  if (rows.length === 0) return <Empty>No scenario runs yet.</Empty>;
  return (
    <TableWrap>
      <table className="tbl">
        <thead>
          <tr>
            <th>#</th>
            <th>At</th>
            <th>Scenario</th>
            <th>Action</th>
            <th>Epoch</th>
            <th>Operator</th>
            <th>Summary</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.run_id}>
              <td>{r.run_id}</td>
              <td>{fmtTime(r.at)}</td>
              <td>
                <Mono>{r.scenario}</Mono>
              </td>
              <td>
                <StatusBadge status={r.action} />
              </td>
              <td>{r.epoch}</td>
              <td>{r.operator}</td>
              <td className="wrap">
                {open === r.run_id ? (
                  <JsonBlock value={r.summary} />
                ) : (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(r.run_id)}>
                    {Object.entries(r.summary ?? {})
                      .map(([k, v]) => `${k}=${String(v)}`)
                      .join(" · ")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}
